import { useMutation } from "@apollo/client";
import { useState } from "react";
import { Button } from "../../../../../components/ui";
import { ASSIGN_VIEWING_ROOMS_TO_SITE } from "../../../../../graphql/mutation/WarehouseSite";
import {
  getMutationError,
  getSuggestedViewingRoomIds,
  isActive,
  toggleAllSelectedIds,
  toggleSelectedId,
} from "../helpers";

const CandidateRow = props => {
  const { candidate, onToggle, selected } = props || {};
  const checkboxId = `assign-viewing-room-${candidate.id}`;

  return (
    <tr className="border-b last:border-b-0">
      <td className="p-2">
        <input
          id={checkboxId}
          type="checkbox"
          className="h-4 w-4"
          checked={selected}
          onChange={() => onToggle(candidate.id)}
        />
      </td>
      <td className="p-2">
        <label htmlFor={checkboxId} className="cursor-pointer">
          {candidate.description}
          {!isActive(candidate) && (
            <span className="pl-2 text-gray-500">(inactive)</span>
          )}
        </label>
      </td>
      <td className="p-2">
        {candidate.currentSiteName || (
          <span className="italic text-gray-500">No site</span>
        )}
      </td>
      <td className="p-2 text-right">{candidate.locationsOnSite}</td>
    </tr>
  );
};

const AssignViewingRoomsForm = props => {
  const { candidates = [], closeModal, siteId, siteName } = props || {};
  const [selectedIds, setSelectedIds] = useState(() =>
    getSuggestedViewingRoomIds(candidates),
  );
  const [error, setError] = useState(null);
  const [assignViewingRoomsToSite, { loading }] = useMutation(
    ASSIGN_VIEWING_ROOMS_TO_SITE,
    { refetchQueries: ["FetchSiteViewingRooms", "FetchSiteLocations"] },
  );

  const candidateIds = candidates.map(candidate => candidate.id);
  const allSelected =
    candidateIds.length > 0 &&
    candidateIds.every(id => selectedIds.includes(id));
  const movingCount = candidates.filter(
    candidate =>
      candidate.currentSiteName && selectedIds.includes(candidate.id),
  ).length;

  const submit = async () => {
    const result = await assignViewingRoomsToSite({
      variables: { input: { ids: selectedIds, siteId } },
    });
    const mutationError = getMutationError(result, "assignViewingRoomsToSite");
    if (mutationError) {
      setError(mutationError);
      return;
    }
    closeModal?.();
  };

  const submitLabel = loading
    ? "Saving"
    : `Assign ${selectedIds.length} to ${siteName || "site"}`;

  return (
    <div className="flex w-full flex-col p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col">
          <div className="text-2xl font-bold">Assign viewing rooms</div>
          <div className="pt-1 text-sm text-gray-500">
            Rooms with locations on this site are selected for you.
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <Button action="default" label="Cancel" onClick={closeModal} />
          <Button
            label={submitLabel}
            disabled={loading || selectedIds.length === 0}
            onClick={submit}
          />
        </div>
      </div>
      {error && (
        <div role="alert" className="pt-4 text-sm text-red-600">
          {error}
        </div>
      )}
      {movingCount > 0 && (
        <div className="pt-4 text-sm text-amber-700">
          {movingCount === 1
            ? "1 selected room"
            : `${movingCount} selected rooms`}{" "}
          will move here from another site.
        </div>
      )}
      <div className="mt-6 max-h-[60vh] overflow-auto pb-6">
        {candidates.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            Every viewing room is already assigned to this site.
          </div>
        ) : (
          <table className="w-full min-w-[600px] text-left text-sm">
            <thead className="border-b font-semibold">
              <tr>
                <th className="w-8 p-2">
                  <input
                    aria-label="Select all viewing rooms"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={allSelected}
                    onChange={() =>
                      setSelectedIds(
                        toggleAllSelectedIds(selectedIds, candidateIds),
                      )
                    }
                  />
                </th>
                <th className="p-2">Viewing room</th>
                <th className="p-2">Current site</th>
                <th className="p-2 text-right">Locations on this site</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map(candidate => (
                <CandidateRow
                  key={candidate.id}
                  candidate={candidate}
                  selected={selectedIds.includes(candidate.id)}
                  onToggle={id =>
                    setSelectedIds(toggleSelectedId(selectedIds, id))
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AssignViewingRoomsForm;
